/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: lookup_specialties entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the lookup_specialties table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the lookup_specialties API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, code, name, sort_order, archived, created_at, updated_at]
 * Searchable fields: []
 * Filterable fields: [id, code]
 */

import { httpClient } from '../http-client.js';
import { type UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  LookupSpecialtiesRow,
  LookupSpecialtiesInsert,
  LookupSpecialtiesUpdate
} from '../../types/generated_schemas/generated-lookup_specialties-schema.js';

export interface LookupSpecialtiesQueryOptions extends UnifiedQueryOptions {}

export interface LookupSpecialtiesResponse {
  data: LookupSpecialtiesRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for lookup_specialties table operations
 *
 * @description Provides type-safe access to lookup_specialties API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class LookupSpecialtiesClient {
  private readonly basePath = '/lookup-specialties';

  /**
   * Get all lookup_specialties records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of lookup_specialties records
   */
  async getAll(options?: LookupSpecialtiesQueryOptions): Promise<LookupSpecialtiesRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sort', options.sortBy);
    if (options?.sortOrder) params.set('order', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<LookupSpecialtiesResponse>(url);
    return response.data;
  }

  /**
   * Get a single lookup_specialties record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the lookup_specialties record
   */
  async getById(id: string): Promise<LookupSpecialtiesRow> {
    const response = await httpClient.get<{
      data: LookupSpecialtiesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new lookup_specialties record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created lookup_specialties record
   */
  async create(data: LookupSpecialtiesInsert): Promise<LookupSpecialtiesRow> {
    const response = await httpClient.post<{
      data: LookupSpecialtiesRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing lookup_specialties record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated lookup_specialties record
   */
  async update(id: string, data: LookupSpecialtiesUpdate): Promise<LookupSpecialtiesRow> {
    const response = await httpClient.put<{
      data: LookupSpecialtiesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a lookup_specialties record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple lookup_specialties records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created lookup_specialties records
   */
  async createMany(data: LookupSpecialtiesInsert[]): Promise<LookupSpecialtiesRow[]> {
    const response = await httpClient.post<{
      data: LookupSpecialtiesRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Search lookup_specialties with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: LookupSpecialtiesQueryOptions
  ): Promise<LookupSpecialtiesResponse> {
    throw new Error('Search operations are not available for lookup_specialties - no searchable fields configured');
  }

  /**
   * Get count of lookup_specialties records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a lookup_specialties record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'lookup_specialties',
      publicFields: ['id', 'code', 'name', 'sort_order', 'archived', 'created_at', 'updated_at'],
      searchableFields: [],
      filterableFields: ['id', 'code'],
      sortableFields: ['sort_order', 'created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at'],
      requiredFields: ['id', 'code', 'name', 'sort_order', 'archived', 'updated_at'],
    };
  }
}

// Export singleton instance for convenience
export const lookupSpecialtiesClient = new LookupSpecialtiesClient();
